/**
 * TimesheetDashboard.jsx — Phase 83 (tabs 1-2 replaced by "Insights" in 84.41)
 *
 * Tabbed analytics dashboard for admin/CM roles. Every tab respects the
 * shared date-range filter at the top. Target = 8.5 hours per person per
 * WORKING day (Sat/Sun + org holidays excluded — computed backend-side).
 *
 * Tabs:
 *   1. Insights      — admin/founder only. Recharts-style Team/Monthly
 *                      distribution + Bulk export, matching the Leaves page.
 *   2. By person     — logged vs target vs shortfall, wide list-view
 *   3. By activity   — hours per project × milestone/task (nested)
 *   4. History       — sparkline per person, red days = below-target
 *
 * All numbers come from a single call to /api/timesheet/dashboard, except
 * the Insights tab which calls /api/timesheet/insights (whole-year shape).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import usePermissions from "@/lib/usePermissions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/Layout";
import { CalendarRange, Loader2, Target, TrendingDown, TrendingUp, Users, ChevronDown, ChevronRight } from "lucide-react";
import TimesheetInsights from "@/components/TimesheetInsights";
import TimesheetExportDialog from "@/components/TimesheetExportDialog";

const TABS = [
  { id: "insights", label: "Insights" },
  { id: "by_person", label: "By person" },
  { id: "by_activity", label: "By activity" },
  { id: "history", label: "History" },
];

const DAILY_TARGET_MIN = 510;   // 8.5h — mirrors backend constant
const DAILY_TARGET_HRS = 8.5;

function fmtHM(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}
function fmtH(mins) {
  return `${((mins || 0) / 60).toFixed(1)}h`;
}

export default function TimesheetDashboard({ initialDate }) {
  const can = usePermissions();
  const isAdmin = can("timesheet_admin");
  const today = initialDate || new Date().toISOString().slice(0, 10);
  const oneWeekAgo = new Date(new Date(today) - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [start, setStart] = useState(oneWeekAgo);
  const [end, setEnd] = useState(today);
  const [tab, setTab] = useState(isAdmin ? "insights" : "by_person");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [singleDay, setSingleDay] = useState(false);  // "today only" quick toggle

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/timesheet/dashboard", { params: { start, end } });
      setData(d);
    } catch (e) {
      logger.warn("Timesheet dashboard fetch failed:", e?.message || e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  // Quick presets
  const applyPreset = (kind) => {
    const t = new Date().toISOString().slice(0, 10);
    if (kind === "today") { setStart(t); setEnd(t); setSingleDay(true); return; }
    setSingleDay(false);
    const d = new Date(t);
    if (kind === "7d") d.setDate(d.getDate() - 6);
    if (kind === "30d") d.setDate(d.getDate() - 29);
    if (kind === "mtd") d.setDate(1);
    setStart(d.toISOString().slice(0, 10));
    setEnd(t);
  };

  const summary = data?.summary || {};
  const targetPerPerson = data?.target_minutes_per_person || 0;

  return (
    <div className="card-flat mb-6" data-testid="timesheet-dashboard">
      {/* Filter bar */}
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarRange size={14} className="text-[#F54800]" />
          <div className="font-display font-bold text-slate-900 text-sm">Timesheet analytics</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Label className="label-overline">From</Label>
            <Input
              type="date"
              value={start}
              max={end}
              onChange={(e) => { setStart(e.target.value); setSingleDay(false); }}
              className="rounded-sm h-8 w-40 text-xs"
              data-testid="tsd-start"
            />
            <Label className="label-overline ml-1">To</Label>
            <Input
              type="date"
              value={end}
              min={start}
              onChange={(e) => { setEnd(e.target.value); setSingleDay(false); }}
              className="rounded-sm h-8 w-40 text-xs"
              data-testid="tsd-end"
            />
          </div>
          <div className="flex items-center gap-1">
            <PresetButton onClick={() => applyPreset("today")} active={singleDay} testid="tsd-preset-today">Today</PresetButton>
            <PresetButton onClick={() => applyPreset("7d")} testid="tsd-preset-7d">7d</PresetButton>
            <PresetButton onClick={() => applyPreset("30d")} testid="tsd-preset-30d">30d</PresetButton>
            <PresetButton onClick={() => applyPreset("mtd")} testid="tsd-preset-mtd">MTD</PresetButton>
          </div>
          {isAdmin && <TimesheetExportDialog />}
        </div>
      </div>

      {/* Summary strip */}
      <div className="px-5 py-4 border-b border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tsd-summary">
        <SummaryStat
          icon={<Users size={12} className="text-slate-400" />}
          label="Team hours"
          value={fmtHM(summary.total_minutes)}
          sub={data ? `${data.working_day_count} working day${data.working_day_count === 1 ? "" : "s"}` : "—"}
          testid="tsd-summary-total"
        />
        <SummaryStat
          icon={<Target size={12} className="text-slate-400" />}
          label={`Target per person (${DAILY_TARGET_HRS}h × ${data?.working_day_count || 0}d)`}
          value={fmtHM(targetPerPerson)}
          testid="tsd-summary-target"
        />
        <SummaryStat
          icon={<TrendingUp size={12} className="text-emerald-600" />}
          label="On target"
          value={summary.on_target_users ?? 0}
          sub={`of ${summary.users_total || 0} team members`}
          tone="emerald"
          testid="tsd-summary-on-target"
        />
        <SummaryStat
          icon={<TrendingDown size={12} className="text-red-600" />}
          label="Short of target"
          value={summary.short_users ?? 0}
          sub={targetPerPerson === 0 ? "0h target (no working days)" : "below 8.5h/day avg"}
          tone="red"
          testid="tsd-summary-short"
        />
      </div>

      {/* Tabs */}
      <div className="px-5 pt-3 border-b border-slate-100 flex items-center gap-1 overflow-x-auto" data-testid="tsd-tabs">
        {TABS.filter((t) => t.id !== "insights" || isAdmin).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === t.id ? "border-[#F54800] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`tsd-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Panels */}
      <div className="p-5 min-h-[280px]">
        {tab === "insights" && isAdmin && <TimesheetInsights />}
        {tab !== "insights" && loading && !data && (
          <div className="flex items-center justify-center py-8 text-xs text-slate-400">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        )}
        {tab !== "insights" && !loading && !data && (
          <div className="text-xs text-slate-400 italic">Could not load dashboard.</div>
        )}
        {data && tab === "by_person" && <ByPersonTab data={data} />}
        {data && tab === "by_activity" && <ByActivityTab data={data} />}
        {data && tab === "history" && <HistoryTab data={data} />}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
function PresetButton({ children, onClick, active, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 px-2.5 text-xs rounded-sm border transition-colors ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function SummaryStat({ icon, label, value, sub, tone, testid }) {
  const valueColor = tone === "emerald" ? "text-emerald-700" : tone === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
        {icon} {label}
      </div>
      <div className={`font-display text-2xl font-bold mt-0.5 ${valueColor}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Tab 3 — By person: logged vs target vs shortfall                    */
/* Feb 2026 · Task #5 — rows are now expandable. Click a person to     */
/* reveal an inline drill-down (day range + project/milestone break-  */
/* down) directly below the row. Other rows push down; no navigation. */
/* ─────────────────────────────────────────────────────────────────── */
function ByPersonTab({ data }) {
  const rows = data.by_person;
  const targetMin = data.target_minutes_per_person;
  const [expandedId, setExpandedId] = useState(null);
  return (
    <div data-testid="tsd-by-person">
      <div className="text-xs text-slate-500 mb-3">
        Present hours per person vs {fmtHM(targetMin)} target ({data.working_day_count} working days).
        Click a row to expand the daily / project / activity breakdown.
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            <th className="label-overline px-3 py-2 text-left w-6"></th>
            {["EMPLOYEE", "LOGGED", "TARGET", "SHORTFALL", "STATUS"].map((h) => (
              <th key={h} className="label-overline px-3 py-2 text-left whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => {
            const isOpen = expandedId === r.user_id;
            return (
              <React.Fragment key={r.user_id}>
                <tr
                  className={`cursor-pointer transition-colors ${isOpen ? "bg-orange-50/40" : "hover:bg-slate-50"}`}
                  data-testid={`tsd-person-row-${r.user_id}`}
                  onClick={() => setExpandedId(isOpen ? null : r.user_id)}
                >
                  <td className="px-3 py-2 text-slate-400">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </td>
                  <td className="px-3 py-2 text-slate-800 font-medium">
                    {r.user_name}
                    {r.business_unit && <span className="text-[10px] text-slate-400 ml-1">· {r.business_unit}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-700">{fmtHM(r.minutes)}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{fmtHM(r.target_minutes)}</td>
                  <td className="px-3 py-2 font-mono">
                    {r.shortfall_minutes > 0
                      ? <span className="text-red-700" data-testid={`tsd-person-short-${r.user_id}`}>-{fmtHM(r.shortfall_minutes)}</span>
                      : <span className="text-emerald-700">0m</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.on_target ? <Tag tone="green">On target</Tag> :
                      targetMin === 0 ? <Tag tone="slate">—</Tag> : <Tag tone="orange">Short</Tag>}
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`tsd-person-expand-${r.user_id}`}>
                    <td colSpan={6} className="p-0 bg-slate-50 border-t border-slate-200">
                      <PersonDrilldown
                        userId={r.user_id}
                        userName={r.user_name}
                        defaultStart={data.start}
                        defaultEnd={data.end}
                        onClose={() => setExpandedId(null)}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-slate-400">No team members to show.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feb 2026 · Per-person drill-down (Task #5)                                 */
/* -------------------------------------------------------------------------- */
/** Fetches /api/timesheet/person/{uid} and renders a range picker + a
 *  project → milestone breakdown right beneath the clicked row. Defaults to
 *  the current week per the user's preference; the range picker lets them
 *  jump to today / month / MTD / custom without leaving the dashboard. */
function PersonDrilldown({ userId, userName, defaultStart, defaultEnd }) {
  const today = new Date().toISOString().slice(0, 10);
  // Feb 2026 · fix — the drill-down now inherits the parent Timesheet
  // Analytics date range on first open (spec: "Respect the top date/range
  // filter"). Users can still narrow further via the Day / Week / 30d / MTD
  // chips or the custom date inputs; those actions clear the "inherited"
  // preset marker so the top filter can still be reapplied from scratch.
  const currentWeek = useMemo(() => {
    const t = new Date(today);
    const day = (t.getDay() + 6) % 7;   // 0 = Mon
    const mon = new Date(t);
    mon.setDate(mon.getDate() - day);
    return { start: mon.toISOString().slice(0, 10), end: today };
  }, [today]);

  const initial = useMemo(() => {
    // Prefer the parent's top date range; fall back to the current ISO week.
    if (defaultStart && defaultEnd) {
      return { start: defaultStart, end: defaultEnd, preset: "inherit" };
    }
    return { ...currentWeek, preset: "week" };
  }, [defaultStart, defaultEnd, currentWeek]);

  const [range, setRange] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/timesheet/person/${userId}`, {
        params: { start: range.start, end: range.end },
      });
      setData(d);
    } catch (e) {
      logger.warn("person drilldown failed:", e?.message || e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, range.start, range.end]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (kind) => {
    const t = new Date().toISOString().slice(0, 10);
    if (kind === "day") return setRange({ start: t, end: t, preset: "day" });
    if (kind === "week") return setRange({ ...currentWeek, preset: "week" });
    if (kind === "inherit" && defaultStart && defaultEnd) {
      return setRange({ start: defaultStart, end: defaultEnd, preset: "inherit" });
    }
    const d = new Date(t);
    if (kind === "month") d.setDate(d.getDate() - 29);
    if (kind === "mtd") d.setDate(1);
    setRange({ start: d.toISOString().slice(0, 10), end: t, preset: kind });
  };

  // Feb 2026 · fix — spec requires the drill-down to "respect the top
  // date/range filter". We render an "Inherit top range" chip whenever a
  // parent range was supplied so the user can jump back to it after
  // exploring a narrower window (e.g. drill into a single day).
  const presets = useMemo(() => {
    const base = [
      { k: "day", label: "Today" },
      { k: "week", label: "This week" },
      { k: "month", label: "30d" },
      { k: "mtd", label: "MTD" },
    ];
    if (defaultStart && defaultEnd) base.unshift({ k: "inherit", label: "Top range" });
    return base;
  }, [defaultStart, defaultEnd]);

  return (
    <div className="p-4" data-testid={`tsd-person-drilldown-${userId}`}>
      {/* Range chips + custom picker */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mr-1">Range</span>
        {presets.map((p) => (
          <button
            key={p.k}
            type="button"
            onClick={() => applyPreset(p.k)}
            className={`h-7 px-2 text-[11px] rounded-sm border transition-colors ${
              range.preset === p.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`tsd-person-preset-${userId}-${p.k}`}
          >
            {p.label}
          </button>
        ))}
        <Input
          type="date"
          value={range.start}
          max={range.end}
          onChange={(e) => setRange({ ...range, start: e.target.value, preset: "custom" })}
          className="rounded-sm h-7 w-36 text-xs"
          data-testid={`tsd-person-start-${userId}`}
        />
        <span className="text-[11px] text-slate-400">→</span>
        <Input
          type="date"
          value={range.end}
          min={range.start}
          onChange={(e) => setRange({ ...range, end: e.target.value, preset: "custom" })}
          className="rounded-sm h-7 w-36 text-xs"
          data-testid={`tsd-person-end-${userId}`}
        />
        <div className="ml-auto text-[11px] text-slate-600 font-mono" data-testid={`tsd-person-total-${userId}`}>
          {data ? fmtHM(data.total_minutes) : "—"} logged
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-4 text-xs text-slate-400">
          <Loader2 size={12} className="animate-spin mr-2" /> Loading {userName}&apos;s breakdown…
        </div>
      )}

      {!loading && data && data.by_activity.length === 0 && (
        <div className="text-xs text-slate-400 italic text-center py-4">
          No time logged in this range.
        </div>
      )}

      {!loading && data && data.by_activity.length > 0 && (
        <PersonStackedView
          data={data}
          userId={userId}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Feb 2026 · Stacked-bar person drill-down (per-user redesign)                */
/* -------------------------------------------------------------------------- */
/** Two-level, low-clutter view:                                              *
 *  ── legend + stacked vertical bars per day (default) with 8.5h target line *
 *  ── click a day OR a project chip to expand a detail panel BELOW           *
 *  Consolidates all "General" entries (milestone_id absent) into one row     *
 *  per project so the tree isn't a sea of duplicated "General" labels. */
// Fixed palette — first N projects (by minutes) map onto these in order so
// colours stay stable while the range is being changed.
const PROJECT_PALETTE = [
  "#F54800", "#0EA5E9", "#10B981", "#8B5CF6", "#F59E0B",
  "#EC4899", "#22C55E", "#3B82F6", "#EF4444", "#14B8A6",
  "#F97316", "#6366F1",
];

// One weekday label per date — used only when the range fits inside a week
// so the x-axis stays scannable. Longer ranges fall back to MM-DD.
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function PersonStackedView({ data, userId }) {
  // `detail` drives the expand-below panel. shape: {kind: "day"|"project", key}
  const [detail, setDetail] = useState(null);

  // Reset the expand panel whenever the person's range changes so we don't
  // leak a stale open state (e.g. clicking Today after being expanded on a
  // week-view day that no longer exists).
  useEffect(() => { setDetail(null); }, [data.start, data.end, data.user_id]);

  const chart = useMemo(() => buildChartModel(data), [data]);
  const targetMin = DAILY_TARGET_MIN;                              // 510m at 8.5h

  return (
    <div data-testid={`tsd-person-stacked-${userId}`}>
      {/* ── Legend ── */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`tsd-person-legend-${userId}`}>
        {chart.projects.map((p) => {
          const isActive = detail?.kind === "project" && detail.key === p.id;
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => setDetail(isActive ? null : { kind: "project", key: p.id })}
              className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] rounded-sm border transition-colors ${
                isActive
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`tsd-person-legend-${userId}-${p.id}`}
              title={`${p.name} — click to drill into milestone / activity`}
            >
              <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: p.color }} aria-hidden="true" />
              <span className="truncate max-w-[140px]">{p.name}</span>
              <span className={`font-mono text-[10px] ${isActive ? "text-white/80" : "text-slate-500"}`}>{fmtHM(p.total)}</span>
            </button>
          );
        })}
      </div>

      {/* ── Stacked vertical bars ── */}
      <StackedDayChart
        chart={chart}
        targetMin={targetMin}
        activeDetail={detail}
        onDayClick={(iso) => setDetail((d) => d?.kind === "day" && d.key === iso ? null : { kind: "day", key: iso })}
        testId={`tsd-person-chart-${userId}`}
      />

      {/* ── Expand-below detail panel ── */}
      {detail && (
        <div
          className="mt-3 bg-white border border-slate-200 rounded-sm p-3"
          data-testid={`tsd-person-detail-${userId}`}
        >
          <PersonDetailPanel
            chart={chart}
            data={data}
            detail={detail}
            testIdPrefix={`tsd-person-detail-${userId}`}
            onClose={() => setDetail(null)}
          />
        </div>
      )}
    </div>
  );
}

/** Aggregates the raw drill-down payload into a chart-friendly model:      *
 *  - projects: sorted-by-total, colour-assigned                             *
 *  - days: total + per-project minutes                                      *
 *  - milestones per project: consolidated (empty milestone_id => General) */
function buildChartModel(data) {
  const projTotals = new Map();
  const projMeta = new Map();
  // Milestone consolidation: key `${pid}::${milestone_id||''}` → aggregate
  const milestoneAgg = new Map();

  for (const row of data.by_activity || []) {
    const pid = row.project_id || "__unlinked__";
    projTotals.set(pid, (projTotals.get(pid) || 0) + row.minutes);
    if (!projMeta.has(pid)) {
      projMeta.set(pid, {
        name: row.project_name || "Unlinked",
        businessUnit: row.business_unit || null,
      });
    }
    const mkey = `${pid}::${row.milestone_id || ""}`;
    if (!milestoneAgg.has(mkey)) {
      milestoneAgg.set(mkey, {
        projectId: pid,
        milestoneTitle: row.milestone_title || "General",
        minutes: 0,
        entries: [],
      });
    }
    const bucket = milestoneAgg.get(mkey);
    bucket.minutes += row.minutes;
    bucket.entries.push(...(row.entries || []));
  }

  const sortedProjects = [...projTotals.entries()].sort((a, b) => b[1] - a[1]);
  const projects = sortedProjects.map(([id, total], idx) => ({
    id,
    name: projMeta.get(id)?.name,
    businessUnit: projMeta.get(id)?.businessUnit,
    total,
    color: PROJECT_PALETTE[idx % PROJECT_PALETTE.length],
  }));
  const projectColor = new Map(projects.map((p) => [p.id, p.color]));

  // Per-day aggregation. Seed from data.days so gaps show as empty bars, then
  // fold in each entry so we get per-project totals per day. If the response
  // omits zero-hour days, fill in every date in the [data.start .. data.end]
  // range so the chart always shows the full "shape of the week / range".
  const daysMap = new Map((data.days || []).map((d) => [d.date, { total: 0, projects: new Map() }]));
  if (data.start && data.end) {
    const cur = new Date(`${data.start}T00:00:00`);
    const end = new Date(`${data.end}T00:00:00`);
    while (cur <= end) {
      const iso = cur.toISOString().slice(0, 10);
      if (!daysMap.has(iso)) daysMap.set(iso, { total: 0, projects: new Map() });
      cur.setDate(cur.getDate() + 1);
    }
  }
  for (const row of data.by_activity || []) {
    const pid = row.project_id || "__unlinked__";
    for (const e of row.entries || []) {
      const bucket = daysMap.get(e.date) || { total: 0, projects: new Map() };
      bucket.total += e.minutes;
      bucket.projects.set(pid, (bucket.projects.get(pid) || 0) + e.minutes);
      daysMap.set(e.date, bucket);
    }
  }
  const days = [...daysMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, val]) => ({
      date,
      total: val.total,
      segments: [...val.projects.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([pid, mins]) => ({ projectId: pid, minutes: mins, color: projectColor.get(pid) })),
    }));

  // Milestone list per project (consolidated).
  const milestonesByProject = new Map();
  for (const bucket of milestoneAgg.values()) {
    if (!milestonesByProject.has(bucket.projectId)) milestonesByProject.set(bucket.projectId, []);
    milestonesByProject.get(bucket.projectId).push(bucket);
  }
  for (const list of milestonesByProject.values()) list.sort((a, b) => b.minutes - a.minutes);

  return { projects, days, projectColor, milestonesByProject };
}

/** Vertical stacked bar chart with dashed 8.5h target overlay. Bars share a
 *  fixed pixel height; a bar's fill % is minutes / chartMax (target or the
 *  tallest day, whichever is larger — so the target line always fits). */
function StackedDayChart({ chart, targetMin, activeDetail, onDayClick, testId }) {
  const chartMax = Math.max(targetMin, ...chart.days.map((d) => d.total), 60);
  const targetPct = (targetMin / chartMax) * 100;
  const useWeekdayLabels = chart.days.length <= 7;

  return (
    <div className="relative bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid={testId}>
      {/* Chart area (fixed height) */}
      <div className="relative flex items-end gap-1.5 h-40 px-1" role="group" aria-label="Daily hours stacked by project">
        {/* Dashed target line (absolute overlay) */}
        <div
          className="absolute left-0 right-0 border-t-2 border-dashed border-slate-500 pointer-events-none z-10"
          style={{ bottom: `${targetPct}%` }}
          aria-hidden="true"
        >
          <span className="absolute -top-4 right-1 text-[9px] text-slate-500 bg-white px-1 rounded-sm">{fmtH(targetMin)} target</span>
        </div>

        {chart.days.map((d) => {
          const isActive = activeDetail?.kind === "day" && activeDetail.key === d.date;
          const heightPct = (d.total / chartMax) * 100;
          return (
            <button
              type="button"
              key={d.date}
              onClick={() => onDayClick(d.date)}
              className={`relative flex-1 flex flex-col justify-end min-w-[24px] h-full rounded-sm transition-colors ${
                isActive ? "bg-slate-200/60 ring-1 ring-[#F54800]" : "hover:bg-slate-100"
              }`}
              data-testid={`${testId}-day-${d.date}`}
              aria-label={`${d.date}: ${fmtHM(d.total)} logged. Click to expand breakdown.`}
              aria-pressed={isActive}
              title={`${d.date} — ${fmtHM(d.total)} total. Click to drill in.`}
            >
              {/* Stack (bottom-up) */}
              <div className="w-full flex flex-col-reverse" style={{ height: `${Math.max(heightPct, 0)}%` }}>
                {d.segments.map((s) => (
                  <div
                    key={s.projectId}
                    style={{
                      height: `${(s.minutes / d.total) * 100}%`,
                      background: s.color,
                    }}
                    className="w-full first:rounded-t-sm"
                    title={`${projectNameFor(chart, s.projectId)} — ${fmtHM(s.minutes)}`}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* X-axis day labels */}
      <div className="flex items-center gap-1.5 mt-1 px-1">
        {chart.days.map((d, i) => {
          const dt = new Date(`${d.date}T00:00:00`);
          const label = useWeekdayLabels
            ? WEEKDAY_SHORT[dt.getDay()]
            : `${dt.getDate().toString().padStart(2, "0")}-${(dt.getMonth() + 1).toString().padStart(2, "0")}`;
          return (
            <div
              key={i}
              className="flex-1 text-center text-[10px] text-slate-500 min-w-[24px]"
              data-testid={`${testId}-label-${d.date}`}
            >
              <div className="font-medium">{label}</div>
              <div className="font-mono text-[9px] text-slate-400">{d.total > 0 ? fmtHM(d.total) : "—"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Detail panel that renders below the chart when a day or project chip is
 *  clicked. Renders one section, not the whole tree. */
function PersonDetailPanel({ chart, data, detail, testIdPrefix, onClose }) {
  if (detail.kind === "day") return <DayDetail chart={chart} data={data} iso={detail.key} testIdPrefix={testIdPrefix} onClose={onClose} />;
  if (detail.kind === "project") return <ProjectDetail chart={chart} projectId={detail.key} testIdPrefix={testIdPrefix} onClose={onClose} />;
  return null;
}

function DayDetail({ chart, data, iso, testIdPrefix, onClose }) {
  const day = chart.days.find((d) => d.date === iso);
  // Rebuild milestone rows scoped to this date only.
  const perProject = useMemo(() => {
    const map = new Map();
    for (const row of data.by_activity || []) {
      const pid = row.project_id || "__unlinked__";
      const dayEntries = (row.entries || []).filter((e) => e.date === iso);
      if (dayEntries.length === 0) continue;
      const mins = dayEntries.reduce((n, e) => n + (e.minutes || 0), 0);
      if (!map.has(pid)) map.set(pid, { projectId: pid, minutes: 0, milestones: [] });
      const bucket = map.get(pid);
      bucket.minutes += mins;
      bucket.milestones.push({
        title: row.milestone_title || "General",
        minutes: mins,
        entries: dayEntries,
      });
    }
    // Consolidate same-title milestones inside a project (backend already
    // dedupes by milestone_id, but this is a safety net + matches the spec's
    // "do not repeat General as many separate rows" ask).
    for (const bucket of map.values()) {
      const dedup = new Map();
      for (const m of bucket.milestones) {
        if (!dedup.has(m.title)) dedup.set(m.title, { title: m.title, minutes: 0, entries: [] });
        const d = dedup.get(m.title);
        d.minutes += m.minutes;
        d.entries.push(...m.entries);
      }
      bucket.milestones = [...dedup.values()].sort((a, b) => b.minutes - a.minutes);
    }
    return [...map.values()].sort((a, b) => b.minutes - a.minutes);
  }, [data, iso]);

  const dt = new Date(`${iso}T00:00:00`);
  const humanDate = dt.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });

  return (
    <div>
      <DetailHeader
        title={humanDate}
        total={day?.total || 0}
        onClose={onClose}
        testId={`${testIdPrefix}-day-header`}
      />
      {perProject.length === 0 ? (
        <div className="text-xs text-slate-400 italic text-center py-3">No time logged on this day.</div>
      ) : (
        <div className="space-y-2">
          {perProject.map((pj) => {
            const proj = chart.projects.find((p) => p.id === pj.projectId);
            return (
              <ProjectMilestoneList
                key={pj.projectId}
                projectName={proj?.name || "Unlinked"}
                projectColor={proj?.color || "#94A3B8"}
                total={pj.minutes}
                milestones={pj.milestones}
                testIdPrefix={`${testIdPrefix}-day-${iso}-${pj.projectId}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ProjectDetail({ chart, projectId, testIdPrefix, onClose }) {
  const proj = chart.projects.find((p) => p.id === projectId);
  const milestones = (chart.milestonesByProject.get(projectId) || []).map((m) => ({
    title: m.milestoneTitle,
    minutes: m.minutes,
    entries: m.entries,
  }));

  return (
    <div>
      <DetailHeader
        title={proj?.name || "Unlinked"}
        subtitle={proj?.businessUnit ? `· ${proj.businessUnit}` : ""}
        total={proj?.total || 0}
        color={proj?.color}
        onClose={onClose}
        testId={`${testIdPrefix}-project-header`}
      />
      {milestones.length === 0 ? (
        <div className="text-xs text-slate-400 italic text-center py-3">No milestone activity in this range.</div>
      ) : (
        <ProjectMilestoneList
          projectName={null}   /* header already showed project name */
          projectColor={proj?.color || "#94A3B8"}
          total={proj?.total || 0}
          milestones={milestones}
          testIdPrefix={`${testIdPrefix}-project-${projectId}`}
        />
      )}
    </div>
  );
}

function DetailHeader({ title, subtitle, total, color, onClose, testId }) {
  return (
    <div className="flex items-baseline justify-between mb-2 pb-1.5 border-b border-slate-100" data-testid={testId}>
      <div className="flex items-baseline gap-2">
        {color && <span className="w-2 h-2 rounded-sm" style={{ background: color }} aria-hidden="true" />}
        <span className="text-sm font-medium text-slate-800">{title}</span>
        {subtitle && <span className="text-[10px] text-slate-400">{subtitle}</span>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-slate-700">{fmtHM(total)}</span>
        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700 text-xs"
          aria-label="Collapse detail"
          data-testid={`${testId}-close`}
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** Reusable milestone list. Renders each milestone as a click-to-expand row
 *  (chevron + title + mini-bar + minutes). Entries appear only on click so
 *  the panel stays low-clutter by default. */
function ProjectMilestoneList({ projectName, projectColor, total, milestones, testIdPrefix }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
      {projectName && (
        <div className="flex items-baseline gap-2 mb-1.5">
          <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: projectColor }} aria-hidden="true" />
          <span className="text-xs font-medium text-slate-800 truncate">{projectName}</span>
          <span className="ml-auto text-[11px] font-mono text-slate-600">{fmtHM(total)}</span>
        </div>
      )}
      <div className="space-y-0.5">
        {milestones.map((m, idx) => {
          const isOpen = openIdx === idx;
          const pct = total > 0 ? (m.minutes / total) * 100 : 0;
          return (
            <div key={idx} data-testid={`${testIdPrefix}-milestone-${idx}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : idx)}
                className="w-full text-left grid grid-cols-12 gap-2 items-center px-1 py-1 rounded-sm hover:bg-white"
                aria-expanded={isOpen}
              >
                <div className="col-span-1 text-slate-400">
                  {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                </div>
                <div className="col-span-5 text-[11px] text-slate-700 truncate">{m.title}</div>
                <div className="col-span-4 h-2 bg-slate-200 rounded-sm overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, background: projectColor }} />
                </div>
                <div className="col-span-2 text-right text-[10px] font-mono text-slate-600">{fmtHM(m.minutes)}</div>
              </button>
              {isOpen && m.entries.length > 0 && (
                <ul className="ml-4 mt-0.5 mb-1.5 space-y-0.5" data-testid={`${testIdPrefix}-milestone-${idx}-entries`}>
                  {m.entries
                    .slice()
                    .sort((a, b) => (a.date < b.date ? 1 : -1))
                    .map((e, i) => (
                      <li key={i} className="text-[10px] text-slate-500 flex items-center gap-2">
                        <span className="font-mono text-slate-400 shrink-0">{e.date.slice(5)}</span>
                        <span className="font-mono text-slate-700 shrink-0">{fmtHM(e.minutes)}</span>
                        <span className="truncate flex-1">
                          {e.note || <span className="italic text-slate-400">no note</span>}
                        </span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Tiny helper used by the tooltip on stacked segments.
function projectNameFor(chart, projectId) {
  return chart.projects.find((p) => p.id === projectId)?.name || "Unlinked";
}

/* ─────────────────────────────────────────────────────────────────── */
/* Tab 4 — By activity/task: grouped by project → milestone            */
/* ─────────────────────────────────────────────────────────────────── */
function ByActivityTab({ data }) {
  const rows = data.by_activity;
  // Feb 2026 · redesign — show project summary rows collapsed by default.
  // Click a project to expand its milestone/activity list. Same visual
  // pattern as ByProjectTab so users learn one interaction.
  const [expandedId, setExpandedId] = useState(null);
  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of rows) {
      const pid = r.project_id || "__unlinked__";
      if (!g.has(pid)) g.set(pid, { projectId: pid, projectName: r.project_name, businessUnit: r.business_unit, total: 0, milestones: new Map() });
      const bucket = g.get(pid);
      const title = r.milestone_title || "General";
      if (!bucket.milestones.has(title)) bucket.milestones.set(title, { title, minutes: 0, entries: [] });
      const m = bucket.milestones.get(title);
      m.minutes += r.minutes;
      m.entries.push(...(r.entries || []));
      bucket.total += r.minutes;
    }
    return [...g.values()]
      .map((b) => ({ ...b, milestones: [...b.milestones.values()].sort((a, b) => b.minutes - a.minutes) }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);
  const grandTotal = grouped.reduce((n, g) => n + g.total, 0) || 1;
  const maxMin = Math.max(...grouped.map((g) => g.total), 60);

  return (
    <div data-testid="tsd-by-activity">
      <div className="text-xs text-slate-500 mb-3">
        Hours grouped by project — click a project to drill into its milestone / activity split.
      </div>
      <div className="space-y-1">
        {grouped.map((bucket) => {
          const pct = (bucket.total / maxMin) * 100;
          const isOpen = expandedId === bucket.projectId;
          return (
            <React.Fragment key={bucket.projectId}>
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : bucket.projectId)}
                className={`w-full text-left grid grid-cols-12 gap-2 items-center px-1 py-1 rounded-sm transition-colors ${isOpen ? "bg-orange-50/60" : "hover:bg-slate-50"}`}
                data-testid={`tsd-activity-project-${bucket.projectId}`}
                aria-expanded={isOpen}
              >
                <div className="col-span-4 flex items-center gap-1 text-xs text-slate-800 truncate" title={bucket.projectName}>
                  <span className="text-slate-400 shrink-0">{isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}</span>
                  <span className="truncate font-medium">{bucket.projectName}</span>
                  {bucket.businessUnit && <span className="text-[10px] text-slate-400 ml-1 shrink-0">· {bucket.businessUnit}</span>}
                </div>
                <div className="col-span-6 relative h-4 bg-slate-100 rounded-sm overflow-hidden">
                  <div className="absolute left-0 top-0 h-full bg-slate-400" style={{ width: `${pct}%` }} />
                </div>
                <div className="col-span-2 text-right text-xs">
                  <span className="font-mono text-slate-700">{fmtHM(bucket.total)}</span>
                  <span className="text-[10px] text-slate-500 ml-1">({Math.round((bucket.total / grandTotal) * 100)}%)</span>
                </div>
              </button>
              {isOpen && (
                <div className="pl-6 pr-1 pb-2" data-testid={`tsd-activity-expand-${bucket.projectId}`}>
                  <ProjectMilestoneList
                    projectName={null}
                    projectColor="#64748B"
                    total={bucket.total}
                    milestones={bucket.milestones}
                    testIdPrefix={`tsd-activity-${bucket.projectId}`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {grouped.length === 0 && (
        <div className="text-xs text-slate-400 italic text-center py-8">No activity logged in this range.</div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */
/* Tab 5 — History: per-person sparkline of working days               */
/* ─────────────────────────────────────────────────────────────────── */
function HistoryTab({ data }) {
  const wds = data.working_days || [];
  const rows = data.history || [];
  return (
    <div data-testid="tsd-history">
      <div className="text-xs text-slate-500 mb-3">
        Daily fill for every working day in the range. <span className="text-red-700 font-medium">Red</span> cells fell short of {DAILY_TARGET_HRS}h.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left px-2 py-2 label-overline whitespace-nowrap">Employee</th>
              {wds.map((d) => (
                <th key={d} className="text-center px-1 py-2 whitespace-nowrap">
                  <div className="text-[9px] text-slate-500">{d.slice(5)}</div>
                </th>
              ))}
              <th className="text-right px-2 py-2 label-overline whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.user_id} data-testid={`tsd-history-row-${r.user_id}`}>
                <td className="px-2 py-1.5 text-slate-800 whitespace-nowrap">{r.user_name}</td>
                {wds.map((d) => {
                  const mins = r.days[d] || 0;
                  const short = mins < DAILY_TARGET_MIN;
                  const isEmpty = mins === 0;
                  return (
                    <td key={d} className="px-1 py-1.5 text-center">
                      <div
                        className={`inline-block w-11 h-6 rounded-sm text-[10px] font-mono flex items-center justify-center leading-none ${
                          isEmpty ? "bg-slate-50 text-slate-300" :
                          short ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"
                        }`}
                        title={`${d}: ${fmtHM(mins)}${short && !isEmpty ? ` — short by ${fmtHM(DAILY_TARGET_MIN - mins)}` : ""}`}
                        data-testid={`tsd-history-cell-${r.user_id}-${d}`}
                      >
                        {isEmpty ? "—" : `${(mins / 60).toFixed(1)}`}
                      </div>
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right font-mono">
                  <span className={r.short_days.length > 0 ? "text-red-700" : "text-emerald-700"}>
                    {fmtHM(r.total_minutes)}
                  </span>
                  {r.short_days.length > 0 && (
                    <div className="text-[9px] text-red-600" data-testid={`tsd-history-short-days-${r.user_id}`}>
                      {r.short_days.length} short day{r.short_days.length === 1 ? "" : "s"}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={wds.length + 2} className="px-2 py-8 text-center text-slate-400 italic">No team members to show.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
